import {
    installControlTracking,
    safeControlProperties,
    type ControlContext,
    type ControlProperties,
} from "./controlAnalytics";
import type { ControlAction } from "./controlActions";
import { carryLanding, readLanding, type Landing } from "./campaign";
import { isOpaqueScriptError } from "./opaqueError";

/**
 * Product analytics, through PostHog.
 *
 * Sent: page views, page leaves, autocaptured clicks, rage and dead
 * clicks, heatmaps, web vitals, uncaught errors, session replay and the
 * named {@link AnalyticsEvent}s. Kept in the browser: every element's
 * text and attributes in an autocaptured click, all text and every input
 * in a replay, request headers and bodies, console logs, and every
 * address but its route's pattern, so a search's words, a posting's id
 * and an email link's token never leave. A person is identified by their
 * WorkOS id alone, never by name or email. Do Not Track turns it all off.
 *
 * Nothing starts on its own. `configureAnalytics` only remembers the
 * key; PostHog starts in `allowAnalytics`, once a visitor says yes in
 * the banner or a person's record says GRANTED, or, outside the places
 * that ask first, while nobody said no (`components/consent`). Session
 * replay runs only on a yes or a GRANTED record, wherever the person is:
 * analytics on by default sends events and records nothing. `stopAnalytics` ends capture and recording at
 * once and opts PostHog out, on a withdrawal or a record that is not
 * GRANTED. Masking is no reason to skip the question.
 *
 * With no key (a local build, the sample build, the tests) every call
 * here does nothing.
 *
 * PostHog is its own chunk, fetched when it starts: it was a third of
 * the first bundle, and a phone drew nothing until that bundle had run.
 * What is asked of it while the chunk loads waits, in order, and is done
 * once it lands; a stop asked meanwhile still lands after the calls
 * before it and before any after it.
 */

export type AnalyticsEvent =
    | { name: "envoy_saved"; first: boolean }
    | { name: "envoy_paused"; reason: string }
    | { name: "envoy_resumed" }
    | { name: "envoy_area_saved" }
    | { name: "posting_queued"; posting_id: string }
    | { name: "posting_unqueued"; posting_id: string }
    | { name: "posting_saved"; posting_id: string }
    | { name: "posting_unsaved"; posting_id: string }
    | { name: "application_marked_sent"; application_id: string }
    | { name: "application_skipped"; application_id: string; reason: string }
    | { name: "tailor_requested"; application_id: string }
    | { name: "resume_uploaded" }
    | { name: "resume_saved" }
    | { name: "checkout_opened"; kind: "plan" | "pack"; item: string }
    | {
          name: "purchase_completed";
          kind: "plan" | "pack";
          /** `pro_month` for a plan, the tailors for a pack; null when the return names none. */
          item: string | null;
          value: number | null;
          currency: string | null;
      }
    | { name: "signed_up" }
    | { name: "billing_portal_opened" }
    | { name: "export_requested" }
    | { name: "history_exported"; rows: number }
    | { name: "sign_in_opened"; screen: "sign-up" | "sign-in"; via: SignInVia };

/** Which button on the Create your envoy dialog sent a visitor to AuthKit. */
export type SignInVia = "google" | "link";

let active = false;
let started = false;
/** Whether session replay is running: only on a yes. */
let recordingOn = false;
let surface: "app" | "site" = "app";
let config: AnalyticsConfig | null = null;
/** The person said no on this page: a grant read back from a record does not undo it. */
let refusedHere = false;
/** Told each time capture comes on or goes off: the ad partners follow it (lib/ads). */
const watchers = new Set<() => void>();
/** The campaign tags the page loaded with, in memory until PostHog starts or the person says no. */
let landing = landingHere();

function landingHere(): Landing | null {
    if (typeof window === "undefined") return null;
    return readLanding(window.location.href, document.referrer);
}

type PostHog = (typeof import("posthog-js"))["default"];

/** PostHog, once its chunk has loaded. */
let client: PostHog | null = null;
/** What was asked of PostHog while its chunk loaded; null once it failed to load. */
let waiting: ((posthog: PostHog) => void)[] | null = [];

/** Runs `call` on PostHog now if it has loaded, or once it does. */
function withPosthog(call: (posthog: PostHog) => void): void {
    if (client) call(client);
    else waiting?.push(call);
}

function loadPosthog(): void {
    import("posthog-js").then(
        ({ default: loaded }) => {
            client = loaded;
            const calls = waiting ?? [];
            waiting = [];
            for (const call of calls) call(loaded);
        },
        // Analytics is lost for this page, and nothing else is: the
        // waiting calls are dropped and later ones are never kept.
        () => {
            waiting = null;
        },
    );
}

export interface AnalyticsConfig {
    key: string | null;
    host: string;
    surface?: "app" | "site";
}

/** Remembers where analytics would go. Starts nothing: that waits for consent. */
export function configureAnalytics(next: AnalyticsConfig): void {
    config = next;
    surface = next.surface ?? "app";
}

/** Whether there is anywhere to send analytics, so whether to ask at all. */
export function analyticsConfigured(): boolean {
    return Boolean(config?.key);
}

/** Whether capture is on now. */
export function analyticsActive(): boolean {
    return active;
}

/** Calls `watcher` each time capture comes on or goes off. Returns the way to stop. */
export function watchAnalytics(watcher: () => void): () => void {
    watchers.add(watcher);
    return () => watchers.delete(watcher);
}

function changed(): void {
    for (const watcher of watchers) watcher();
}

/**
 * Consent is given: PostHog starts, or capture and recording resume.
 * `record` is a grant read back from the auth service, or analytics on by
 * default, which waits while the person has said no on this page;
 * `choice` is the person saying yes now, which always counts. `recording`
 * is whether session replay may run: false for analytics on by default,
 * and replay starts or stops to match when capture is already on.
 */
export function allowAnalytics(by: "record" | "choice" = "record", recording = true): void {
    if (by === "choice") refusedHere = false;
    else if (refusedHere) return;
    if (!config?.key) return;
    if (!active) {
        if (!started) {
            startPosthog(config.key, config.host, recording);
            started = true;
            recordingOn = recording;
            installControlTracking(trackControl);
        }
        // An opt out is kept by PostHog in the browser, from this page or
        // an earlier visit: a yes now lifts it, without an event of its own.
        withPosthog((posthog) => {
            if (posthog.has_opted_out_capturing())
                posthog.opt_in_capturing({ captureEventName: false });
        });
        active = true;
        changed();
    }
    if (recording === recordingOn) return;
    recordingOn = recording;
    withPosthog((posthog) => {
        if (recording) posthog.startSessionRecording();
        else posthog.stopSessionRecording();
    });
}

/**
 * No consent, or consent withdrawn: recording and capture stop at once,
 * and PostHog is opted out so its own handlers send nothing more. `choice`
 * is the person saying no now; a grant read from a record cannot restart
 * it until they say yes again.
 */
export function stopAnalytics(by: "record" | "choice" = "record"): void {
    if (by === "choice") {
        refusedHere = true;
        landing = null;
    }
    // Off already: it was opted out when it went off.
    if (!active) return;
    active = false;
    recordingOn = false;
    withPosthog((posthog) => {
        posthog.stopSessionRecording();
        posthog.opt_out_capturing();
    });
    changed();
}

/**
 * For the tests: as the page loads, with nothing started. With `loaded`,
 * PostHog's chunk counts as landed, so a test sees each call at once.
 */
export function resetAnalyticsForTests(loaded: PostHog | null = null): void {
    client = loaded;
    waiting = [];
    active = false;
    started = false;
    recordingOn = false;
    refusedHere = false;
    config = null;
    surface = "app";
    landing = landingHere();
}

function startPosthog(key: string, host: string, recording: boolean): void {
    if (!client) loadPosthog();
    withPosthog((posthog) => initPosthog(posthog, key, host, recording));
}

function initPosthog(posthog: PostHog, key: string, host: string, recording: boolean): void {
    posthog.init(key, {
        api_host: host,
        // Where PostHog's own app is. With api_host on our reverse proxy,
        // posthog-js cannot tell, and the toolbar's links would point at it.
        ui_host: "https://eu.posthog.com",
        defaults: "2026-05-30",
        autocapture: true,
        capture_pageview: false,
        capture_pageleave: true,
        capture_exceptions: true,
        capture_performance: { web_vitals: true, network_timing: true },
        capture_heatmaps: true,
        capture_dead_clicks: true,
        rageclick: true,
        disable_session_recording: !recording,
        enable_recording_console_log: false,
        session_recording: {
            maskAllInputs: true,
            maskTextSelector: "*",
            recordHeaders: false,
            recordBody: false,
            // Every address the replay keeps, the page's and each request's,
            // goes through here.
            maskCapturedNetworkRequestFn: (request) => {
                const name = scrubUrl(request.name, window.location.origin);
                return typeof name === "string" ? { ...request, name } : null;
            },
        },
        disable_surveys: true,
        person_profiles: "identified_only",
        respect_dnt: true,
        // An ad's click id goes out as `<masked>`; the campaign tags as they are.
        mask_personal_data_properties: true,
        mask_all_text: true,
        mask_all_element_attributes: true,
        // PostHog adds the page's whole address to every event, and the
        // first one seen to the person; each goes out as its route's
        // pattern. An error the browser hid is dropped (lib/opaqueError).
        before_send: (event) =>
            event && !isOpaqueScriptError(event)
                ? {
                      ...event,
                      properties: scrubProperties(event.properties, window.location.origin),
                      $set: event.$set && scrubProperties(event.$set, window.location.origin),
                      $set_once:
                          event.$set_once &&
                          scrubProperties(event.$set_once, window.location.origin),
                  }
                : null,
    });
    if (landing) carryLanding(posthog, landing, window.location.href);
    landing = null;
}

/** Ties later events to the person, by their WorkOS id. */
export function identify(userId: string): void {
    if (!active) return;
    withPosthog((posthog) => posthog.identify(userId));
}

/** Forgets the person, on sign out. */
export function resetAnalytics(): void {
    if (!active) return;
    withPosthog((posthog) => posthog.reset());
}

/** An error a boundary caught, which PostHog's own handlers never see. */
export function captureError(error: unknown): void {
    if (!active) return;
    withPosthog((posthog) => posthog.captureException(error));
}

export function track(event: AnalyticsEvent): void {
    if (!active) return;
    const { name, ...properties } = event;
    withPosthog((posthog) => posthog.capture(name, properties));
}

/** A named attempt at a control. Business success is recorded separately by track. */
export function trackControl(
    name: ControlAction,
    properties: ControlProperties | ControlContext,
): void {
    if (!active) return;
    const scalars = safeControlProperties(properties);
    if (typeof scalars.target === "string") {
        try {
            scalars.target = String(
                scrubUrl(
                    new URL(scalars.target, window.location.origin).href,
                    window.location.origin,
                ),
            );
        } catch {
            delete scalars.target;
        }
    }
    const route = routePattern(window.location.pathname);
    withPosthog((posthog) => posthog.capture(name, { ...scalars, surface, route }));
}

/**
 * The route's pattern for a path: each id becomes its parameter's name,
 * so `/jobs/posting/7d2e…` is `/jobs/posting/:postingId`. A path not listed keeps its
 * first segment only.
 */
export function routePattern(pathname: string): string {
    const segments = pathname.split("/").filter(Boolean);
    if (segments.length === 0) return "/";
    const [head, second, third] = segments;
    if (head === "jobs" && second === "saved") return "/jobs/saved";
    if (head === "jobs" && second === "posting") return "/jobs/posting/:postingId";
    if (head === "resume" && second)
        return `/resume/${second === "edit" || second === "work" ? second : ":part"}`;
    if (head === "email") return "/email/choices";
    if (head === "tools" && second === "resume-keyword-checker")
        return `/tools/resume-keyword-checker${third ? "/:postingId" : ""}`;
    return `/${head}`;
}

/** The properties PostHog fills with an address of its own accord. */
const URL_PROPERTIES = new Set([
    "$current_url",
    "$referrer",
    "$session_entry_url",
    "$session_entry_referrer",
    "$initial_current_url",
    "$initial_referrer",
    "navigationURL",
]);

const PATH_PROPERTIES = new Set([
    "$pathname",
    "$session_entry_pathname",
    "$initial_pathname",
    "$prev_pageview_pathname",
]);

/**
 * An address as analytics keeps it: this site's as its route's pattern,
 * another site's as its origin alone, and anything else as nothing.
 */
export function scrubUrl(raw: unknown, origin: string): unknown {
    if (typeof raw !== "string" || raw === "" || raw === "$direct") return raw;
    try {
        const url = new URL(raw);
        return url.origin === origin ? `${origin}${routePattern(url.pathname)}` : url.origin;
    } catch {
        return null;
    }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Every address in a set of event properties, scrubbed, in nested
 * objects too (a web vital's own record of the page); everything else
 * as it was. Heatmap data is keyed by address, so its keys are scrubbed
 * and the points of two addresses that now match are merged. An error
 * has its message and stack scrubbed.
 */
export function scrubProperties<T extends Record<string, unknown>>(
    properties: T,
    origin: string,
): T {
    return Object.fromEntries(
        Object.entries(properties).map(([key, value]) => {
            if (URL_PROPERTIES.has(key)) return [key, scrubUrl(value, origin)];
            if (PATH_PROPERTIES.has(key) && typeof value === "string")
                return [key, routePattern(value)];
            if (key === "$heatmap_data" && isPlainObject(value))
                return [key, scrubHeatmap(value, origin)];
            if (key === "$exception_list" && Array.isArray(value))
                return [key, value.map((exception) => scrubException(exception, origin))];
            if (isPlainObject(value)) return [key, scrubProperties(value, origin)];
            return [key, value];
        }),
    ) as T;
}

function scrubHeatmap(data: Record<string, unknown>, origin: string): Record<string, unknown[]> {
    const scrubbed: Record<string, unknown[]> = {};
    for (const [url, points] of Object.entries(data)) {
        const key = scrubUrl(url, origin);
        if (typeof key !== "string" || !Array.isArray(points)) continue;
        (scrubbed[key] ??= []).push(...points);
    }
    return scrubbed;
}

const URL_IN_TEXT = /https?:\/\/[^\s"'<>()]+/g;
const EMAIL_IN_TEXT = /[^\s@"'<>()]+@[^\s@"'<>()]+\.[a-z]{2,}/gi;

/**
 * An error's message is free text, so any address in it is scrubbed and
 * any email cut. A stack frame keeps a built file's path, which the
 * stack is read against, and any other address is scrubbed.
 */
function scrubException(exception: unknown, origin: string): unknown {
    if (!isPlainObject(exception)) return exception;
    const scrubbed: Record<string, unknown> = { ...exception };
    if (typeof scrubbed.value === "string")
        scrubbed.value = scrubbed.value
            .replace(URL_IN_TEXT, (url) => String(scrubUrl(url, origin) ?? "[address]"))
            .replace(EMAIL_IN_TEXT, "[email]");
    const stacktrace = scrubbed.stacktrace;
    if (isPlainObject(stacktrace) && Array.isArray(stacktrace.frames))
        scrubbed.stacktrace = {
            ...stacktrace,
            frames: stacktrace.frames.map((frame) => scrubFrame(frame, origin)),
        };
    return scrubbed;
}

function scrubFrame(frame: unknown, origin: string): unknown {
    if (!isPlainObject(frame)) return frame;
    const scrubbed: Record<string, unknown> = { ...frame };
    for (const key of ["filename", "abs_path"]) {
        const value = scrubbed[key];
        if (typeof value !== "string") continue;
        try {
            const url = new URL(value);
            scrubbed[key] =
                url.origin === origin && url.pathname.startsWith("/assets/")
                    ? `${origin}${url.pathname}`
                    : scrubUrl(value, origin);
        } catch {
            // Not an address: a frame from the browser itself, kept.
        }
    }
    return scrubbed;
}

/** A page view with the route's pattern and nothing else of the address. */
export function trackPageview(pathname: string, origin: string): void {
    if (!active) return;
    const pattern = routePattern(pathname);
    withPosthog((posthog) =>
        posthog.capture("$pageview", { $current_url: `${origin}${pattern}`, $pathname: pattern }),
    );
}
